/**
 * Data held — every figure this source stored, across a chosen span of years.
 *
 * ── What this tab is, and what it deliberately is not ────────────────────────
 * It is a DATA INSPECTOR, not a validation screen. Reading the backend showed
 * that four of the five things `/fields` returns per entry barely vary:
 * `impact` is a constant per severity, `reason` is the Findings tab's own issue
 * label, `origin` is effectively always `reported`, and `validation` is
 * `not_reviewed` for anything the bounded agent digest never reached. A screen
 * organised around any of them is organised around a column with no variance —
 * which is why the previous version showed seventy grey squares, three
 * identical bars and a 4% coverage figure, and told a reader nothing.
 *
 * What this endpoint uniquely holds is the field NAMES and the actual VALUES.
 * So that is the screen: statement, field, and the number in each year. The
 * handful of cells a finding named get a marker, and the rest of the validation
 * story stays on the Findings tab where its own data lives.
 *
 * ── One request, a windowed view ─────────────────────────────────────────────
 * `getFieldCoverage` omits the `year` param when passed null, and the backend
 * then returns every stored year — so a scale jump or a year that stopped being
 * reported is visible, which it never is one year at a time. But eleven columns
 * at once is unreadable, so the SPAN is a display choice: the same 3Y/5Y/7Y/10Y
 * control the Finance tab uses, defaulting to five. Fetching everything once is
 * what makes paging through the window instant.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Search, X } from 'lucide-react';
import Input from '../../../ui/Input';
import Select from '../../../ui/Select';
import YearWindowSelector from '../../../ui/YearWindowSelector';
import {
  clampWindow,
  yearWindowWorthShowing,
  type YearWindowSize,
} from '../../../ui/yearWindow';
import type { ValidationChain } from '../../../../types/dataQuality';
import { useFieldCoverage } from '../../hooks/useDataQuality';
import FieldPivotTable from '../FieldPivotTable';
import { chainLabel } from '../dqSelectors';
import {
  buildPivot,
  findingsAccountedFor,
  flaggedRowCount,
  matchesQuery,
  sortRows,
  type FieldSort,
} from '../fieldCoverage';

export interface FieldsViewProps {
  companyId: string;
  chain: ValidationChain;
  /** A canonical field name to ring and scroll to, from a finding's chip. */
  highlightField?: string | null;
}

const SORT_LABEL: Record<FieldSort, string> = {
  graph: 'Graph order',
  flagged: 'Flagged first',
};

/** Five years plus the field and note columns is seven — a readable table. */
const DEFAULT_WINDOW = 5;

export default function FieldsView({
  companyId,
  chain,
  highlightField,
}: FieldsViewProps) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<FieldSort>('graph');
  const [windowSize, setWindowSize] = useState<number>(DEFAULT_WINDOW);
  const [windowOffset, setWindowOffset] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  // `null` year — every stored year in one response. See the header note.
  const { data, isLoading, isError } = useFieldCoverage(
    companyId,
    chain.source,
    null,
    true
  );

  const pivot = useMemo(() => buildPivot(data), [data]);
  const flagged = useMemo(() => flaggedRowCount(pivot), [pivot]);

  const totalYears = pivot.years.length;
  const showSelector = yearWindowWorthShowing(totalYears);

  // Clamped rather than stored clamped, so switching source to a chain with
  // fewer years cannot leave the window pointing past the end of the data.
  const { size: effectiveSize, offset: effectiveOffset } = clampWindow(
    totalYears,
    showSelector ? windowSize : totalYears,
    windowOffset
  );

  // Newest first, so offset 0 is the most recent span.
  const visibleYears = useMemo(
    () => pivot.years.slice(effectiveOffset, effectiveOffset + effectiveSize),
    [pivot.years, effectiveOffset, effectiveSize]
  );

  // Actionable findings only. `info` notes describe the SOURCE rather than the
  // data, so a field-level table cannot be expected to account for them.
  const expectedFindingCount =
    (chain.summary.error_count ?? 0) + (chain.summary.warning_count ?? 0);

  const accountedFor = findingsAccountedFor({
    fieldCount: pivot.fieldCount,
    expectedFindingCount,
    flaggedRows: flagged,
  });

  const searching = query.trim().length > 0;

  const statements = useMemo(
    () =>
      pivot.statements
        .map((statement) => {
          const matching = searching
            ? statement.rows.filter((row) => matchesQuery(row, query))
            : statement.rows;
          return {
            ...statement,
            visible: sortRows(matching, sort),
            total: statement.rows.length,
          };
        })
        .filter((statement) => statement.visible.length > 0),
    [pivot.statements, query, searching, sort]
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      // Never steal a slash the reader is trying to type.
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Bring the row the reader followed a field chip to into view, once.
  useEffect(() => {
    if (!highlightField || pivot.fieldCount === 0) return;
    const row = document.getElementById(`dq-field-${highlightField}`);
    // Feature-detected: this runs in a PASSIVE EFFECT, so an environment
    // without `scrollIntoView` would throw here with no boundary above this
    // component and unmount the whole advisory panel. The ring still works.
    if (typeof row?.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlightField, pivot.fieldCount]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-sm text-neutral-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading the stored figures…
      </div>
    );
  }

  // 404 and 503 both resolve to null in the API layer: advisory detail that is
  // unavailable is a quiet sentence, not an error state.
  if (isError || pivot.fieldCount === 0) {
    return (
      <p className="py-2 text-sm text-neutral-500">
        No stored figures are available for this source.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {/* The Finance tab's toolbar shell, so search, sort and the year span sit
          on one line instead of each claiming its own. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2.5">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-neutral-800">
            Data held — {chainLabel(chain)}
          </h4>
          <p className="mt-0.5 text-xs text-neutral-500">
            {pivot.fieldCount} field{pivot.fieldCount === 1 ? '' : 's'} · {totalYears}{' '}
            reporting period{totalYears === 1 ? '' : 's'}
            {showSelector && effectiveSize < totalYears
              ? ` · showing ${effectiveSize}`
              : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="w-60">
            <Input
              ref={searchRef}
              type="search"
              aria-label="Search fields"
              title="Press / to focus"
              placeholder="Search fields"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
              rightIcon={
                query ? (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                    className="rounded text-neutral-400 hover:text-neutral-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : undefined
              }
              className="py-1.5 text-sm"
            />
          </div>

          <div className="w-40">
            <Select
              aria-label="Sort fields"
              value={sort}
              onChange={(event) => setSort(event.target.value as FieldSort)}
              options={(['graph', 'flagged'] as const).map((value) => ({
                value,
                label: SORT_LABEL[value],
              }))}
              className="py-1.5 text-sm"
            />
          </div>

          {/* Hidden below four years: every option would be wider than the
              data, so the control would be four dead struck-through buttons. */}
          {showSelector && (
            <YearWindowSelector
              totalYears={totalYears}
              size={effectiveSize}
              onSizeChange={(next: YearWindowSize) => setWindowSize(next)}
              offset={effectiveOffset}
              onOffsetChange={setWindowOffset}
              firstYear={visibleYears[visibleYears.length - 1]}
              lastYear={visibleYears[0]}
            />
          )}
        </div>
      </div>

      {/* One line of validation context, and no more. When the summary reports
          findings this payload pins to nothing, an unmarked table would imply
          the figures were checked and cleared — so say which it is. */}
      {!accountedFor ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertTriangle className="mt-px h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
          <p className="text-xs leading-relaxed text-amber-900">
            The checks raised {expectedFindingCount} finding
            {expectedFindingCount === 1 ? '' : 's'} for this source, but none of them is
            attributed to a field here — so the figures below carry no check marks, not
            because they passed. See the Findings tab for what was raised.
          </p>
        </div>
      ) : flagged > 0 ? (
        <p className="text-xs text-neutral-600">
          <span className="text-red-600" aria-hidden="true">
            ⚠
          </span>{' '}
          <strong className="font-semibold">{flagged}</strong> of {pivot.fieldCount} fields are
          named by a finding — marked below, and explained on the Findings tab.
        </p>
      ) : (
        <p className="text-xs text-neutral-500">No finding names any individual field.</p>
      )}

      {statements.length === 0 ? (
        <p className="rounded-lg border border-dashed border-neutral-200 py-8 text-center text-sm text-neutral-500">
          No field name matches “{query}”.
        </p>
      ) : (
        <div className="space-y-2">
          {statements.map((statement, index) => (
            <FieldPivotTable
              key={statement.statement}
              label={statement.label}
              rows={statement.visible}
              years={visibleYears}
              totalRows={statement.total}
              highlightField={highlightField}
              // The first statement opens so the tab is never a wall of closed
              // panels; a search opens everything it matched; and a statement
              // the reader was linked into opens itself.
              open={
                index === 0 ||
                searching ||
                (!!highlightField &&
                  statement.visible.some((row) => row.canonical === highlightField))
              }
            />
          ))}
        </div>
      )}

      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-neutral-100 pt-2 text-[11px] text-neutral-400">
        <span>
          <span className="text-red-600" aria-hidden="true">
            ⚠
          </span>{' '}
          named by a finding
        </span>
        <span>
          <span className="text-amber-600" aria-hidden="true">
            ⚑
          </span>{' '}
          a zero standing in for a missing value
        </span>
        <span>· a period that never held this field</span>
        <span>— no value stored</span>
      </p>
    </div>
  );
}
